import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { UserDetail as UserDetailType } from '../../types/api';
import { userService } from '../../services/userService';
import { sharedService, type UploadedFile } from '../../services/sharedService';
import { getVariantPriceByCurrency, planService } from '../../services/planService';
import type { AdminSubscriptionPlan, PlanCurrencyCode, PlanDetailResponse } from '../../types/api';
import { useSharedStore } from '../../store/sharedStore';
import { 
  InfoCard, 
  LoadingState, 
  ErrorState,
  PageHeader,
  Button,
  Input,
  PhoneInput,
  Select,
  Spinner,
  PhotoCropModal,
  ImageLightbox,
  Badge,
} from '../../components';
import type { SelectOption } from '../../components';
import { 
  PersonIcon,
  HomeIcon,
  BackpackIcon,
  FileTextIcon,
  Pencil1Icon,
  Cross2Icon,
  PlusIcon,
  TrashIcon,
  DownloadIcon,
  ReaderIcon,
  CheckCircledIcon,
  Link2Icon,
  IdCardIcon,
} from '@radix-ui/react-icons';
import { getFullName, getLookupValue } from '../../utils/formatters';
import {
  genderOptions,
  manglikOptions,
  horoscopeOptions,
  formatManglikLabel,
  DEFAULT_PHONE_COUNTRY_CODE,
  buildFullPhoneNumber,
  parsePhoneNumber,
  buildProfilePhotoPayload,
} from './profileFormConstants';
import {
  PROFILE_FIELD_LABELS,
  getMaxDateOfBirthForMinAge,
  type ProfileValidationField,
} from './profileFormValidation';
import { usePhotoCropFlow } from '../../hooks/usePhotoCropFlow';
import { useGalleryPhotoCropUpload } from '../../hooks/useGalleryPhotoCropUpload';
import { useEmailValidation } from '../../hooks/useEmailValidation';
import { useProfileFormValidation } from '../../hooks/useProfileFormValidation';
import { formatApiError, getFieldErrorsFromApi } from '../../utils/errorHandler';
import { ASSETS } from '../../constants/assets';
import { getMockClientProfile } from './mockClientProfile';
import { RmSuggestionsTab } from './components/RmSuggestionsTab';
import { InternalNotesTab } from './components/InternalNotesTab';
import { ClientTasksTab } from './components/ClientTasksTab';
import { getUserDisplayStatus, USER_STATUS_BADGE } from './userStatusConfig';
import { connectionService, formatConnectionDetail } from '../../services/connectionService';
import {
  userInvoiceService,
  formatInvoiceDate,
  formatInvoiceAmount,
  formatInvoiceId,
  formatInvoicePlanName,
  getInvoiceDownloadId,
  triggerBlobDownload,
} from '../../services/userInvoiceService';
import { userTasksService } from '../../services/userTasksService';
import { useAuthStore } from '../../store/authStore';
import { canViewSuggestionsTab } from '../../utils/suggestionPermissions';
import {
  canAccessRoute,
  canAssignClientPlan,
  canEditClientProfile,
  canViewClientTasksTab,
} from '../../utils/rbac';
import type { AdminConnectionCard, ConnectionsTab } from '../../types/connection';
import type { AdminUserInvoiceRow } from '../../types/userInvoice';
import './UserDetail.scss';
import './CreateUser.scss';

interface EditFormData {
  // Personal Info
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneE164: string;
  gender: string;
  dateOfBirth: string;
  // Profile Details
  motherTongueId: string;
  religionId: string;
  maritalStatusId: string;
  heightId: string;
  // Physical Fields
  bodyTypeId: string;
  weight: string;
  complexionId: string;
  bloodGroupId: string;
  disabilityId: string;
  // Dynamic Religion Fields
  sectId: string;
  casteId: string;
  jamatId: string;
  casteNoBar: boolean;
  // Optional Profile Fields
  manglikId: string;
  horoscopeNecessity: string;
  // Career Details
  countryId: string;
  stateId: string;
  cityId: string;
  degreeId: string;
  employedInId: string;
  occupationId: string;
  income_currency_id: string;
  annualIncome: string;
  companyName: string;
  workLocation: string;
  aboutYou: string;
  // Lifestyle
  drinkingHabitId: string;
  smokingHabitId: string;
  dietId: string;
  // Family
  familyTypeId: string;
  fatherOccupationId: string;
  motherOccupationId: string;
  brothers: string;
  marriedBrothers: string;
  sisters: string;
  marriedSisters: string;
  familyCountryId: string;
  familyStateId: string;
  familyCityId: string;
  contactAddress: string;
  aboutFamily: string;
}

interface DynamicFieldsState {
  showSect: boolean;
  showCaste: boolean;
  showJamat: boolean;
  showManglik: boolean;
}

interface PhotoUploadState {
  mainPhoto: UploadedFile | null;
  mainPhotoPreview: string | null;
  galleryPhotos: UploadedFile[];
  galleryPreviews: string[];
}

const MAX_GALLERY_PHOTOS = 4;
const TOTAL_PHOTO_SLOTS = 5;
const CREATE_USER_SECTION_ICONS = ASSETS.icons.createUser;

const getHeightValue = (height: { height: string } | string | null | undefined): string => {
  if (!height) return '—';
  if (typeof height === 'string') return height;
  return height.height || '—';
};

interface IncomeCurrencyData {
  income_currency_id?: string;
  name?: string;
  symbol?: string;
}

const formatAnnualIncome = (currency: IncomeCurrencyData | null | undefined, amount: number | string | null | undefined): string => {
  if (!amount || amount === '0' || amount === 0) return '—';
  const symbol = currency?.symbol || '';
  const numAmount = typeof amount === 'string' ? parseInt(amount) : amount;
  return `${symbol}${numAmount.toLocaleString()}`;
};

const calculateAge = (dateOfBirth?: string): string => {
  if (!dateOfBirth) return '—';
  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return '—';
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return `${age} Years`;
};

const formatJoinedDate = (createdAt?: string | null): string => {
  if (!createdAt) return '—';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const formatPlanDate = (dateValue?: string | null): string => {
  if (!dateValue) return '—';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatManglik = formatManglikLabel;

const displayOrDash = (value?: string | null): string => {
  if (value == null) return '—';
  const trimmed = String(value).trim();
  return trimmed || '—';
};

const formatDateOfBirth = (dateOfBirth?: string): string => {
  if (!dateOfBirth) return '—';
  const date = new Date(dateOfBirth);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatHoroscope = (value?: boolean | null): string => {
  if (value == null) return '—';
  return value ? 'Must' : 'Not Necessary';
};

const formatCasteNoBar = (user: UserDetailType): string => {
  const haveCasteBar = user.basicDetails?.haveCasteBar ?? user.profile?.haveCasteBar;
  if (haveCasteBar == null) return '—';
  return haveCasteBar ? 'Open to all castes' : '—';
};

const formatFamilyLocation = (user: UserDetailType): string => {
  const parts = [
    user.family?.familyLivingCity?.name,
    user.family?.familyLivingState?.name,
    user.family?.family_living_country?.name,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
};

const formatSiblingCount = (count?: string | null, married?: string | null): string => {
  if (count == null || count === '') return '—';
  if (married == null || married === '' || married === '0') return count;
  return `${count} (${married} married)`;
};

const formatHobbies = (hobbies?: string[] | null): string => {
  if (!hobbies?.length) return '—';
  return hobbies.join(', ');
};

const resolveUserPhotos = (
  profileImage?: string | null,
  photos?: string[] | null
): { primaryPhoto: string; gallerySlots: (string | null)[] } => {
  const defaultAvatar = '/assets/images/default-avatar.svg';
  const primary = profileImage || photos?.[0] || defaultAvatar;
  const galleryUrls = Array.from(new Set(
    (photos || []).filter((url) => url && url !== primary)
  )).slice(0, 4);
  const gallerySlots = Array.from({ length: 4 }, (_, index) => galleryUrls[index] ?? null);

  return { primaryPhoto: primary, gallerySlots };
};

interface ClientDetailField {
  label: string;
  value: string;
  fullWidth?: boolean;
}

const ClientDetailFields: React.FC<{ fields: ClientDetailField[] }> = ({ fields }) => (
  <div className="client-detail-grid">
    {fields.map((field) => (
      <div
        key={field.label}
        className={field.fullWidth ? 'client-detail-grid__full' : undefined}
      >
        <span>{field.label}</span>
        <strong>{field.value}</strong>
      </div>
    ))}
  </div>
);


const CONNECTION_TABS: { key: ConnectionsTab; label: string }[] = [
  { key: 'received', label: 'Request Received' },
  { key: 'accepted', label: 'Request Accepted' },
  { key: 'pending', label: 'Request Pending' },
  { key: 'rejected', label: 'Request Rejected' },
];

interface ClientProfileViewProps {
  user: UserDetailType;
  fullName: string;
  profileImage?: string | null;
  onBack: () => void;
  onAssignPlan: () => void;
  onEdit: () => void;
  canEditProfile: boolean;
  canAssignPlan: boolean;
  canViewTasksTab: boolean;
}

type ClientProfileTab = 'details' | 'rm' | 'connections' | 'invoice' | 'tasks' | 'notes';

interface ClientProfileTabItem {
  key: ClientProfileTab;
  label: string;
  count?: string;
  icon?: React.ReactNode;
}

const ClientProfileView: React.FC<ClientProfileViewProps> = ({
  user,
  fullName,
  profileImage,
  onBack,
  onAssignPlan,
  onEdit,
  canEditProfile,
  canAssignPlan,
  canViewTasksTab,
}) => {
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const role = useAuthStore((state) => state.role);
  const clientUserId = user.userId;

  const [activeTab, setActiveTab] = useState<ClientProfileTab>('details');
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const [connectionsTab, setConnectionsTab] = useState<ConnectionsTab>('received');
  const [connections, setConnections] = useState<AdminConnectionCard[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsTotal, setConnectionsTotal] = useState<number | null>(null);
  const [tasksTotal, setTasksTotal] = useState<number | null>(null);

  const [invoiceRows, setInvoiceRows] = useState<AdminUserInvoiceRow[]>([]);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceTotalPages, setInvoiceTotalPages] = useState(1);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [downloadingRef, setDownloadingRef] = useState<string | null>(null);
  const { primaryPhoto, gallerySlots } = resolveUserPhotos(profileImage, user.photos);
  const galleryPhotoCount = gallerySlots.filter(Boolean).length;
  const hasActivePlan = Boolean(user.activePlanDetails?.hasActivePlan);
  const planName = hasActivePlan
    ? (user.activePlanDetails?.planName || '—')
    : 'No active plan';
  const planExpiry = formatPlanDate(user.activePlanDetails?.endDate);
  const verified = Boolean(user.emailVerified || user.phoneVerified);
  const displayStatus = getUserDisplayStatus({
    status: user.status,
    isBlocked: user.isBlocked,
  });
  const statusBadge = displayStatus ? USER_STATUS_BADGE[displayStatus] : null;

  const canViewInvoiceHistory = canAccessRoute(role, 'user-plans');

  const tabs: ClientProfileTabItem[] = [
    { key: 'details', label: 'Profile Details', icon: <PersonIcon aria-hidden="true" /> },
    ...(canViewSuggestionsTab(role)
      ? [{ key: 'rm' as const, label: 'RM Suggestions', icon: <Link2Icon aria-hidden="true" /> }]
      : []),
    {
      key: 'connections',
      label: 'Connections',
      count: connectionsTotal != null ? String(connectionsTotal) : undefined,
      icon: <Link2Icon aria-hidden="true" />,
    },
    ...(canViewInvoiceHistory
      ? [{ key: 'invoice' as const, label: 'Invoice History', icon: <IdCardIcon aria-hidden="true" /> }]
      : []),
    ...(canViewTasksTab
      ? [{
          key: 'tasks' as const,
          label: 'Client Tasks',
          count: tasksTotal != null ? String(tasksTotal) : undefined,
          icon: <CheckCircledIcon aria-hidden="true" />,
        }]
      : []),
    ...(hasPermission('user_mgmt:read')
      ? [{ key: 'notes' as const, label: 'Internal Notes', icon: <ReaderIcon aria-hidden="true" /> }]
      : []),
  ];

  useEffect(() => {
    if (activeTab === 'invoice' && !canViewInvoiceHistory) {
      setActiveTab('details');
    }
  }, [activeTab, canViewInvoiceHistory]);

  useEffect(() => {
    if (!clientUserId) return;
    connectionService
      .getConnectionsTabTotal(clientUserId)
      .then(setConnectionsTotal)
      .catch(() => setConnectionsTotal(null));
  }, [clientUserId]);

  useEffect(() => {
    if (!clientUserId) return;
    userTasksService
      .listTasks(clientUserId)
      .then((rows) => setTasksTotal(rows.length))
      .catch(() => setTasksTotal(null));
  }, [clientUserId]);

  useEffect(() => {
    if (activeTab !== 'connections' || !clientUserId) return;
    let cancelled = false;
    setConnectionsLoading(true);
    connectionService
      .getConnections(clientUserId, connectionsTab)
      .then((rows) => {
        if (!cancelled) setConnections(rows);
      })
      .catch(() => {
        if (!cancelled) setConnections([]);
      })
      .finally(() => {
        if (!cancelled) setConnectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, clientUserId, connectionsTab]);

  useEffect(() => {
    if (activeTab !== 'invoice' || !clientUserId) return;
    let cancelled = false;
    setInvoicesLoading(true);
    userInvoiceService
      .listInvoices(clientUserId, { page: invoicePage, limit: 20 })
      .then((response) => {
        if (cancelled) return;
        setInvoiceRows(response.data);
        setInvoiceTotalPages(response.metaData.totalPages);
      })
      .catch(() => {
        if (!cancelled) setInvoiceRows([]);
      })
      .finally(() => {
        if (!cancelled) setInvoicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, clientUserId, invoicePage]);

  const handleDownloadInvoice = useCallback(
    async (row: AdminUserInvoiceRow) => {
      const downloadId = getInvoiceDownloadId(row);
      if (!downloadId) return;

      const downloadKey = `${row.type}-${downloadId}`;
      setDownloadingRef(downloadKey);
      try {
        const { blob, filename } = await userInvoiceService.downloadInvoicePdf(
          row.type,
          downloadId
        );
        triggerBlobDownload(blob, filename);
        if (!clientUserId) return;
        const response = await userInvoiceService.listInvoices(clientUserId, {
          page: invoicePage,
          limit: 20,
        });
        setInvoiceRows(response.data);
        setInvoiceTotalPages(response.metaData.totalPages);
      } catch (err) {
        console.error('Invoice download failed:', err);
      } finally {
        setDownloadingRef(null);
      }
    },
    [clientUserId, invoicePage]
  );

  const renderConnectionCard = (card: AdminConnectionCard, index: number) => (
    <article className="client-match-card" key={String(card.connectionRequestId)}>
      <div className={`client-match-card__avatar client-match-card__avatar--${(index % 4) + 1}`}>
        <img
          src={card.userImage || '/assets/images/default-avatar.svg'}
          alt=""
        />
      </div>
      <div>
        <h3>{card.name}</h3>
        <p>{formatConnectionDetail(card.age, card.height)}</p>
        {'requestSentBy' in card && card.requestSentBy === 'client' && (
          <span>Sent by client</span>
        )}
      </div>
    </article>
  );

  return (
    <div className="client-profile-view">

      <button type="button" className="client-profile-back" onClick={onBack}>
        &lt; Back to Client List
      </button>

      <section className="client-profile-summary">
        <div className="client-profile-summary__avatar">
          <button
            type="button"
            className="client-profile-summary__avatar-btn"
            onClick={() => setLightboxImage(primaryPhoto)}
            aria-label={`View ${fullName} profile photo`}
          >
            <img src={primaryPhoto} alt={fullName} />
          </button>
        </div>
        <div className="client-profile-summary__identity">
          <div className="client-profile-summary__name-row">
            <h1>{fullName}</h1>
            {statusBadge && (
              <Badge
                variant={statusBadge.variant}
                size="small"
                className="client-profile-status"
              >
                {statusBadge.label}
              </Badge>
            )}
          </div>
          <p>ID: {user.uniqueId || 'S4U-98273'} &bull; Joined {formatJoinedDate(user.createdAt)}</p>
          {verified && (
            <span className="client-profile-verified client-profile-verified--inline">
              Identity Verified
            </span>
          )}
        </div>
        <div className="client-profile-plan">
          <span>Current Membership</span>
          <strong>{planName}</strong>
          {hasActivePlan && <p>expiry: {planExpiry}</p>}
        </div>
        {canAssignPlan && (
        <Button className="client-profile-assign" onClick={onAssignPlan}>
          Assign Plan
        </Button>
        )}
        <span className="client-profile-menu" aria-hidden="true">&#8942;</span>
      </section>

      <nav className="client-profile-tabs" aria-label="Client profile sections">
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            className={activeTab === tab.key ? 'is-active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
            {tab.count && <span>{tab.count}</span>}
          </button>
        ))}
      </nav>

      {activeTab === 'details' && (
        <div className="client-profile-sections">
          <section className="client-profile-card client-profile-card--photos">
            <div className="client-profile-card__header">
              <h2><FileTextIcon /> Profile Photos</h2>
              {canEditProfile && (
              <button type="button" className="client-profile-card__edit" onClick={onEdit} aria-label="Edit profile photos">
                <Pencil1Icon aria-hidden="true" />
              </button>
              )}
            </div>
            <div className="client-photos">
              <div>
                <span>Primary Photo</span>
                <button
                  type="button"
                  className="client-photos__preview-btn"
                  onClick={() => setLightboxImage(primaryPhoto)}
                  aria-label="View primary photo"
                >
                  <img src={primaryPhoto} alt={fullName} className="client-photos__primary" />
                </button>
              </div>
              <div className="client-photos__gallery-wrap">
                <span>Gallery Photos ({galleryPhotoCount})</span>
                <div className="client-photos__gallery">
                  {gallerySlots.map((photo, index) => (
                    photo ? (
                      <button
                        type="button"
                        key={`${photo}-${index}`}
                        className="client-photos__preview-btn"
                        onClick={() => setLightboxImage(photo)}
                        aria-label={`View gallery photo ${index + 1}`}
                      >
                        <img src={photo} alt="" />
                      </button>
                    ) : (
                      <div className="client-photos__placeholder" key={`empty-${index}`} aria-hidden="true" />
                    )
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="client-profile-card">
            <div className="client-profile-card__header">
              <h2><PersonIcon /> Personal Details</h2>
              {canEditProfile && (
              <button type="button" className="client-profile-card__edit" onClick={onEdit} aria-label="Edit personal details">
                <Pencil1Icon aria-hidden="true" />
              </button>
              )}
            </div>
            <ClientDetailFields
              fields={[
                { label: 'Full Name', value: fullName },
                { label: 'Email', value: displayOrDash(user.email) },
                { label: 'Phone Number', value: displayOrDash(user.phoneNumber) },
                { label: 'Gender', value: displayOrDash(user.gender) },
                { label: 'Date of Birth', value: formatDateOfBirth(user.dateOfBirth) },
                { label: 'Age / Height', value: `${calculateAge(user.dateOfBirth)}, ${getHeightValue(user.basicDetails?.height)}` },
                { label: 'Marital Status', value: displayOrDash(getLookupValue(user.basicDetails?.marital_status)) },
                { label: 'Mother Tongue', value: displayOrDash(getLookupValue(user.basicDetails?.motherTongue)) },
                { label: 'Religion', value: displayOrDash(user.basicDetails?.religion?.name) },
                { label: 'Sect', value: displayOrDash(user.basicDetails?.sect?.name) },
                { label: 'Caste', value: displayOrDash(user.basicDetails?.caste?.name) },
                { label: 'Jamat', value: displayOrDash(user.basicDetails?.jamat?.name) },
                { label: 'Caste Preference', value: formatCasteNoBar(user) },
                { label: 'Country', value: displayOrDash(user.basicDetails?.country?.name) },
                { label: 'State', value: displayOrDash(user.basicDetails?.state?.name) },
                { label: 'City', value: displayOrDash(user.basicDetails?.city?.name) },
                { label: 'Manglik Status', value: formatManglik(user.profile?.manglik) },
                { label: 'Horoscope', value: formatHoroscope(user.profile?.isHoroscopeNecessary) },
                { label: 'Body Type', value: displayOrDash(user.profile?.bodyType?.name) },
                { label: 'Weight (kg)', value: displayOrDash(user.profile?.weight) },
                { label: 'Complexion', value: displayOrDash(user.profile?.complexion?.name) },
                { label: 'Blood Group', value: displayOrDash(user.profile?.bloodGroup?.name) },
                { label: 'Disability Status', value: displayOrDash(user.profile?.disability?.name) },
              ]}
            />
          </section>

          <section className="client-profile-card">
            <div className="client-profile-card__header">
              <h2><BackpackIcon /> Career & Education</h2>
              {canEditProfile && (
              <button type="button" className="client-profile-card__edit" onClick={onEdit} aria-label="Edit career and education">
                <Pencil1Icon aria-hidden="true" />
              </button>
              )}
            </div>
            <ClientDetailFields
              fields={[
                { label: 'Country', value: displayOrDash(user.basicDetails?.country?.name) },
                { label: 'State', value: displayOrDash(user.basicDetails?.state?.name) },
                { label: 'City', value: displayOrDash(user.basicDetails?.city?.name) },
                { label: 'Employed In', value: displayOrDash(getLookupValue(user.education?.employedIn)) },
                { label: 'Highest Qualification', value: displayOrDash(getLookupValue(user.education?.highestDegree)) },
                { label: 'Occupation', value: displayOrDash(getLookupValue(user.education?.occupation)) },
                { label: 'Annual Income', value: formatAnnualIncome(user.education?.incomeCurrency, user.education?.annualIncome) },
                { label: 'About You', value: displayOrDash(user.aboutMe), fullWidth: true },
              ]}
            />
          </section>

          <section className="client-profile-card">
            <div className="client-profile-card__header">
              <h2><HomeIcon /> Lifestyle & Family</h2>
              {canEditProfile && (
              <button type="button" className="client-profile-card__edit" onClick={onEdit} aria-label="Edit lifestyle and family">
                <Pencil1Icon aria-hidden="true" />
              </button>
              )}
            </div>
            <ClientDetailFields
              fields={[
                { label: 'Drink', value: displayOrDash(user.lifestyle?.drinkingHabit?.name) },
                { label: 'Smoke', value: displayOrDash(user.lifestyle?.smokingHabit?.name) },
                { label: 'Diet', value: displayOrDash(user.lifestyle?.diet?.name) },
                { label: 'Hobbies', value: formatHobbies(user.lifestyle?.hobby) },
                { label: 'Family Type', value: displayOrDash(getLookupValue(user.family?.familyType)) },
                { label: 'Family Living Location', value: formatFamilyLocation(user) },
                { label: "Father's Occupation", value: displayOrDash(getLookupValue(user.family?.fatherOccupation)) },
                { label: "Mother's Occupation", value: displayOrDash(getLookupValue(user.family?.motherOccupation)) },
                { label: 'Brothers', value: formatSiblingCount(user.family?.brothers, user.family?.brothers_married) },
                { label: 'Sisters', value: formatSiblingCount(user.family?.sisters, user.family?.sisters_married) },
                { label: 'Contact Address', value: displayOrDash(user.family?.address), fullWidth: true },
                { label: 'About Family', value: displayOrDash(user.family?.aboutFamily), fullWidth: true },
              ]}
            />
          </section>
        </div>
      )}

      {activeTab === 'rm' && (
        <RmSuggestionsTab clientUserId={clientUserId} clientName={fullName} />
      )}

      {activeTab === 'connections' && (
        <section className="client-profile-card client-profile-card--matches">
          <div className="client-connection-filters" aria-label="Connection statuses">
            {CONNECTION_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={connectionsTab === tab.key ? 'is-active' : ''}
                onClick={() => setConnectionsTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="client-match-grid">
            {connectionsLoading && <p>Loading connections…</p>}
            {!connectionsLoading && connections.length === 0 && (
              <p>No connections in this tab.</p>
            )}
            {!connectionsLoading && connections.map(renderConnectionCard)}
          </div>
        </section>
      )}

      {activeTab === 'invoice' && canViewInvoiceHistory && (
        <section className="client-profile-card client-profile-card--table">
          <div className="client-profile-card__header">
            <h2><FileTextIcon /> Invoice History</h2>
          </div>
          <div className="client-data-table-wrap">
            <table className="client-data-table client-data-table--invoice">
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  <th>Date</th>
                  <th>Plan Name</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoicesLoading && (
                  <tr>
                    <td colSpan={6}>Loading invoices…</td>
                  </tr>
                )}
                {!invoicesLoading && invoiceRows.length === 0 && (
                  <tr>
                    <td colSpan={6}>No invoices yet.</td>
                  </tr>
                )}
                {!invoicesLoading &&
                  invoiceRows.map((row) => {
                    const invoiceLabel = formatInvoiceId(row);
                    const downloadId = getInvoiceDownloadId(row);
                    const downloadKey = `${row.type}-${downloadId}`;
                    return (
                      <tr key={downloadKey}>
                        <td>
                          <span className="client-data-table__link">{invoiceLabel}</span>
                        </td>
                        <td>{formatInvoiceDate(row.date)}</td>
                        <td>{formatInvoicePlanName(row)}</td>
                        <td className="client-data-table__amount">
                          {formatInvoiceAmount(row.amount, row.currency)}
                        </td>
                        <td>
                          <span className="client-pill client-pill--paid">{row.status}</span>
                        </td>
                        <td className="client-data-table__icon-actions">
                          <button
                            type="button"
                            aria-label={`Download invoice ${invoiceLabel}`}
                            disabled={!downloadId || downloadingRef === downloadKey}
                            onClick={() => handleDownloadInvoice(row)}
                          >
                            <DownloadIcon aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {invoiceTotalPages > 1 && (
            <div className="client-invoice-pagination">
              <button
                type="button"
                disabled={invoicePage <= 1}
                onClick={() => setInvoicePage((p) => p - 1)}
              >
                Previous
              </button>
              <span>
                Page {invoicePage} of {invoiceTotalPages}
              </span>
              <button
                type="button"
                disabled={invoicePage >= invoiceTotalPages}
                onClick={() => setInvoicePage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </section>
      )}

      {activeTab === 'tasks' && (
        <ClientTasksTab
          clientUserId={clientUserId}
          clientName={fullName}
          clientEmail={user.email}
          onCountChange={setTasksTotal}
        />
      )}

      {activeTab === 'notes' && hasPermission('user_mgmt:read') && (
        <InternalNotesTab clientUserId={clientUserId} />
      )}

      {lightboxImage && (
        <ImageLightbox
          imageSrc={lightboxImage}
          alt={fullName}
          onClose={() => setLightboxImage(null)}
        />
      )}

    </div>
  );
};

export const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.role);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canEditProfile = canEditClientProfile(role);
  const canAssignPlan = canAssignClientPlan(role);
  const canViewTasksTab = canViewClientTasksTab(role);
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditRequested = searchParams.get('edit') === 'true';
  const autoEditStartedRef = useRef(false);
  const [user, setUser] = useState<UserDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPreparingEdit, setIsPreparingEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const emailValidation = useEmailValidation();
  const [formData, setFormData] = useState<EditFormData>({
    // Personal Info
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
    phoneNumber: '',
    phoneE164: '',
    gender: '',
    dateOfBirth: '',
    // Profile Details
    motherTongueId: '',
    religionId: '',
    maritalStatusId: '',
    heightId: '',
    // Physical Fields
    bodyTypeId: '',
    weight: '',
    complexionId: '',
    bloodGroupId: '',
    disabilityId: '',
    // Dynamic Religion Fields
    sectId: '',
    casteId: '',
    jamatId: '',
    casteNoBar: false,
    // Optional Profile Fields
    manglikId: '',
    horoscopeNecessity: '',
    // Career Details
    countryId: '',
    stateId: '',
    cityId: '',
    degreeId: '',
    employedInId: '',
    occupationId: '',
    income_currency_id: '',
    annualIncome: '',
    companyName: '',
    workLocation: '',
    aboutYou: '',
    // Lifestyle
    drinkingHabitId: '',
    smokingHabitId: '',
    dietId: '',
    // Family
    familyTypeId: '',
    fatherOccupationId: '',
    motherOccupationId: '',
    brothers: '',
    marriedBrothers: '',
    sisters: '',
    marriedSisters: '',
    familyCountryId: '',
    familyStateId: '',
    familyCityId: '',
    contactAddress: '',
    aboutFamily: '',
  });

  // Photo upload state
  const [photoState, setPhotoState] = useState<PhotoUploadState>({
    mainPhoto: null,
    mainPhotoPreview: null,
    galleryPhotos: [],
    galleryPreviews: [],
  });
  const [isUploadingMainPhoto, setIsUploadingMainPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  
  // File input refs
  const mainPhotoInputRef = useRef<HTMLInputElement>(null);

  // Dynamic fields state
  const [dynamicFields, setDynamicFields] = useState<DynamicFieldsState>({
    showSect: false,
    showCaste: false,
    showJamat: false,
    showManglik: false,
  });

  const fullPhoneNumber = useMemo(
    () =>
      formData.phoneE164 ||
      buildFullPhoneNumber(formData.phoneCountryCode, formData.phoneNumber),
    [formData.phoneE164, formData.phoneCountryCode, formData.phoneNumber]
  );

  const profileValidation = useProfileFormValidation(
    formData,
    dynamicFields,
    fullPhoneNumber
  );

  // Loading states for dependent dropdowns
  const [isLoadingSects, setIsLoadingSects] = useState(false);
  const [isLoadingCastes, setIsLoadingCastes] = useState(false);
  const [isLoadingJamats, setIsLoadingJamats] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingOccupations, setIsLoadingOccupations] = useState(false);
  const [isLoadingFamilyStates, setIsLoadingFamilyStates] = useState(false);
  const [isLoadingFamilyCities, setIsLoadingFamilyCities] = useState(false);

  // Family location data (separate from career location)
  const [familyStates, setFamilyStates] = useState<SelectOption[]>([]);
  const [familyCities, setFamilyCities] = useState<SelectOption[]>([]);

  // Income currencies
  const [incomeCurrencies, setIncomeCurrencies] = useState<SelectOption[]>([]);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);

  // Assign Plan Modal State
  const [isAssignPlanModalOpen, setIsAssignPlanModalOpen] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingPlanDetail, setIsLoadingPlanDetail] = useState(false);
  const [isAssigningPlan, setIsAssigningPlan] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<AdminSubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PlanDetailResponse | null>(null);
  const [assignPlanForm, setAssignPlanForm] = useState({
    planId: '',
    planDetailId: '',
    noteFromAdmin: '',
    currency: 'INR',
    collectionType: 'online' as 'online' | 'cash',
  });
  const [assignPlanError, setAssignPlanError] = useState<string | null>(null);
  const [assignPlanSuccess, setAssignPlanSuccess] = useState<string | null>(null);

  // Get shared lookup data from store
  const {
    // Profile lookups
    motherTongues,
    heights,
    religions,
    maritalStatuses,
    bodyTypes,
    complexions,
    disabilities,
    bloodGroups,
    // Location
    countries,
    // Career lookups
    degrees,
    employedIn,
    // Lifestyle lookups
    drinkingHabits,
    smokingHabits,
    diets,
    // Family lookups
    familyTypes,
    fatherOccupations,
    motherOccupations,
    // Dependent lookups
    sects,
    castes,
    jamats,
    states,
    cities,
    occupations,
    // Actions
    fetchSectsWithMeta,
    fetchCastes,
    fetchJamats,
    fetchStates,
    fetchCities,
    fetchOccupations,
    getReligionMeta,
    getSectMeta,
    clearDependentLookups,
  } = useSharedStore();

  const handleBack = () => navigate('/users');

  // Initialize form data from user
  const initializeFormData = useCallback((userData: UserDetailType) => {
    const normalizedPhone = userData.phoneNumber?.replace(/\s+/g, '') ?? '';
    const { countryCode, localNumber } = parsePhoneNumber(normalizedPhone);
    setFormData({
      // Personal Info
      firstName: userData.firstName || '',
      middleName: userData.middleName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      phoneCountryCode: countryCode,
      phoneNumber: localNumber,
      phoneE164: normalizedPhone,
      gender: userData.gender || '',
      dateOfBirth: userData.dateOfBirth || '',
      // Profile Details
      motherTongueId: userData.basicDetails?.motherTongue?.mother_tongue_id || '',
      religionId: userData.basicDetails?.religion?.religion_id || '',
      maritalStatusId: userData.basicDetails?.marital_status?.marital_status_id || '',
      heightId: userData.basicDetails?.height?.height_id || '',
      // Physical Fields
      bodyTypeId: userData.profile?.bodyType?.body_type_id || '',
      weight: userData.profile?.weight || '',
      complexionId: userData.profile?.complexion?.complexion_id || '',
      bloodGroupId: userData.profile?.bloodGroup?.blood_group_id || '',
      disabilityId: userData.profile?.disability?.disability_id || '',
      // Dynamic Religion Fields
      sectId: userData.basicDetails?.sect?.sect_id || '',
      casteId: userData.basicDetails?.caste?.caste_id || '',
      jamatId: userData.basicDetails?.jamat?.jamat_id || '',
      casteNoBar: userData.profile?.haveCasteBar || userData.basicDetails?.haveCasteBar || false,
      // Optional Profile Fields
      manglikId: userData.profile?.manglik || '',
      horoscopeNecessity: userData.profile?.isHoroscopeNecessary ? 'must' : 'not_necessary',
      // Career Details
      countryId: userData.basicDetails?.country?.country_id || '',
      stateId: userData.basicDetails?.state?.state_id || '',
      cityId: userData.basicDetails?.city?.city_id || '',
      degreeId: userData.education?.highestDegree?.degree_id || '',
      employedInId: userData.education?.employedIn?.employed_in_id || '',
      occupationId: userData.education?.occupation?.occupation_id || '',
      income_currency_id: userData.education?.incomeCurrency?.income_currency_id
        ? String(userData.education.incomeCurrency.income_currency_id)
        : '',
      annualIncome: userData.education?.annualIncome?.toString() || '',
      companyName: userData.education?.companyName || '',
      workLocation: userData.education?.workLocation || '',
      aboutYou: userData.aboutMe || '',
      // Lifestyle
      drinkingHabitId: userData.lifestyle?.drinkingHabit?.drinking_habit_id || '',
      smokingHabitId: userData.lifestyle?.smokingHabit?.smoking_habit_id || '',
      dietId: userData.lifestyle?.diet?.diet_id || '',
      // Family
      familyTypeId: userData.family?.familyType?.family_type_id || '',
      fatherOccupationId: userData.family?.fatherOccupation?.father_occupation_id || '',
      motherOccupationId: userData.family?.motherOccupation?.mother_occupation_id || '',
      brothers: userData.family?.brothers || '',
      marriedBrothers: userData.family?.brothers_married || '',
      sisters: userData.family?.sisters || '',
      marriedSisters: userData.family?.sisters_married || '',
      familyCountryId: userData.family?.family_living_country?.country_id || '',
      familyStateId: userData.family?.familyLivingState?.state_id || '',
      familyCityId: userData.family?.familyLivingCity?.city_id || '',
      contactAddress: userData.family?.address || '',
      aboutFamily: userData.family?.aboutFamily || '',
    });
  }, []);

  // Handle edit mode toggle
  const handleEditClick = async () => {
    if (!canEditProfile) return;
    setIsPreparingEdit(true);
    setFormError(null);
    profileValidation.reset();

    try {
      if (user) {
        initializeFormData(user);
        initializePhotosFromUser(user);

        const religionId = user.basicDetails?.religion?.religion_id;
        if (religionId) {
          const religionMeta = getReligionMeta(religionId);
          if (religionMeta) {
            setDynamicFields({
              showSect: religionMeta.have_sect,
              showCaste: !religionMeta.have_sect && religionMeta.have_caste,
              showJamat: false,
              showManglik: religionMeta.have_manglik,
            });

            if (religionMeta.have_sect) {
              setIsLoadingSects(true);
              await fetchSectsWithMeta(religionId);
              setIsLoadingSects(false);

              const sectId = user.basicDetails?.sect?.sect_id;
              if (sectId) {
                const sectMeta = getSectMeta(sectId);
                if (sectMeta?.have_jamat) {
                  setDynamicFields(prev => ({ ...prev, showJamat: true }));
                  setIsLoadingJamats(true);
                  await fetchJamats(sectId);
                  setIsLoadingJamats(false);
                }
                setIsLoadingCastes(true);
                await fetchCastes(religionId, sectId);
                setIsLoadingCastes(false);
              }
            } else if (religionMeta.have_caste) {
              setIsLoadingCastes(true);
              await fetchCastes(religionId);
              setIsLoadingCastes(false);
            }
          }
        }

        if (user.basicDetails?.country?.country_id) {
          setIsLoadingStates(true);
          await fetchStates(user.basicDetails.country.country_id);
          setIsLoadingStates(false);
        }
        if (user.basicDetails?.state?.state_id) {
          setIsLoadingCities(true);
          await fetchCities(user.basicDetails.state.state_id);
          setIsLoadingCities(false);
        }

        if (user.family?.family_living_country?.country_id) {
          setIsLoadingFamilyStates(true);
          const statesData = await sharedService.getStates(user.family.family_living_country.country_id);
          setFamilyStates(statesData);
          setIsLoadingFamilyStates(false);
        }
        if (user.family?.familyLivingState?.state_id) {
          setIsLoadingFamilyCities(true);
          const citiesData = await sharedService.getCities(user.family.familyLivingState.state_id);
          setFamilyCities(citiesData);
          setIsLoadingFamilyCities(false);
        }

        if (user.education?.employedIn?.employed_in_id) {
          setIsLoadingOccupations(true);
          await fetchOccupations(user.education.employedIn.employed_in_id);
          setIsLoadingOccupations(false);
        }
      }

      setIsEditMode(true);
    } catch (err) {
      console.error('Failed to open editor:', err);
      setFormError(formatApiError(err) || 'Failed to open profile editor.');
    } finally {
      setIsPreparingEdit(false);
    }
  };

  useEffect(() => {
    autoEditStartedRef.current = false;
  }, [userId]);

  useEffect(() => {
    if (!isEditRequested || canEditProfile) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [canEditProfile, isEditRequested, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isEditRequested || !user || isEditMode || autoEditStartedRef.current || !canEditProfile) return;
    autoEditStartedRef.current = true;
    void handleEditClick();
    // handleEditClick is intentionally omitted — run once when edit is requested via URL
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditRequested, user, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !isEditRequested) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [isEditMode, isEditRequested, searchParams, setSearchParams]);

  const handleCancelEdit = () => {
    setIsEditMode(false);
    emailValidation.reset();
    profileValidation.reset();
    setFormError(null);
    setDynamicFields({ showSect: false, showCaste: false, showJamat: false, showManglik: false });
    setFamilyStates([]);
    setFamilyCities([]);
    if (user) {
      initializeFormData(user);
    }
    // Clear dependent lookups
    clearDependentLookups('sects');
    clearDependentLookups('castes');
    clearDependentLookups('jamats');
    clearDependentLookups('states');
    clearDependentLookups('cities');
    clearDependentLookups('occupations');
  };

  const handleInputChange = (field: keyof EditFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
    if (field in PROFILE_FIELD_LABELS) {
      profileValidation.touchOnChange(field as ProfileValidationField);
    }
  };

  const handlePhoneChange = useCallback((value: string) => {
    const { countryCode, localNumber } = parsePhoneNumber(value);
    setFormData((prev) => ({
      ...prev,
      phoneE164: value,
      phoneCountryCode: countryCode,
      phoneNumber: localNumber,
    }));
    setFormError(null);
  }, []);

  // ============ Annual Income Handler ============

  const handleAnnualIncomeChange = useCallback((value: string) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, annualIncome: sanitized }));
    setFormError(null);
    profileValidation.touchOnChange('annualIncome');
  }, [profileValidation]);

  // ============ Fetch Income Currencies ============
  useEffect(() => {
    const fetchCurrencies = async () => {
      setIsLoadingCurrencies(true);
      try {
        const currencies = await sharedService.getCurrencies();
        setIncomeCurrencies(currencies);
      } catch (err) {
        console.error('Failed to fetch income currencies:', err);
      } finally {
        setIsLoadingCurrencies(false);
      }
    };
    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ Religion Dynamic Field Handlers ============

  const handleReligionChange = useCallback(async (religionId: string) => {
    // Reset all religion-dependent fields
    setFormData(prev => ({
      ...prev,
      religionId,
      sectId: '',
      casteId: '',
      jamatId: '',
      casteNoBar: false,
      manglikId: '',
    }));
    profileValidation.touchOnChange('religionId');
    
    // Clear dependent lookups
    clearDependentLookups('sects');
    clearDependentLookups('castes');
    clearDependentLookups('jamats');

    if (!religionId) {
      setDynamicFields({ showSect: false, showCaste: false, showJamat: false, showManglik: false });
      return;
    }

    const religionMeta = getReligionMeta(religionId);
    if (!religionMeta) {
      setDynamicFields({ showSect: false, showCaste: false, showJamat: false, showManglik: false });
      return;
    }

    const { have_sect, have_caste, have_manglik } = religionMeta;

    setDynamicFields({
      showSect: have_sect,
      showCaste: !have_sect && have_caste,
      showJamat: false,
      showManglik: have_manglik,
    });

    if (have_sect) {
      setIsLoadingSects(true);
      await fetchSectsWithMeta(religionId);
      setIsLoadingSects(false);
    }
    
    if (!have_sect && have_caste) {
      setIsLoadingCastes(true);
      await fetchCastes(religionId);
      setIsLoadingCastes(false);
    }
  }, [getReligionMeta, fetchSectsWithMeta, fetchCastes, clearDependentLookups, profileValidation]);

  const handleSectChange = useCallback(async (sectId: string) => {
    setFormData(prev => ({
      ...prev,
      sectId,
      casteId: '',
      jamatId: '',
      casteNoBar: false,
    }));
    profileValidation.touchOnChange('sectId');

    clearDependentLookups('castes');
    clearDependentLookups('jamats');

    if (!sectId || !formData.religionId) {
      setDynamicFields(prev => ({ ...prev, showCaste: false, showJamat: false }));
      return;
    }

    const sectMeta = getSectMeta(sectId);
    const hasJamat = sectMeta?.have_jamat ?? false;

    setDynamicFields(prev => ({
      ...prev,
      showCaste: true,
      showJamat: hasJamat,
    }));

    setIsLoadingCastes(true);
    await fetchCastes(formData.religionId, sectId);
    setIsLoadingCastes(false);

    if (hasJamat) {
      setIsLoadingJamats(true);
      await fetchJamats(sectId);
      setIsLoadingJamats(false);
    }
  }, [formData.religionId, getSectMeta, fetchCastes, fetchJamats, clearDependentLookups, profileValidation]);

  // ============ Location Handlers ============

  const handleCountryChange = useCallback(async (countryId: string) => {
    setFormData(prev => ({
      ...prev,
      countryId,
      stateId: '',
      cityId: '',
    }));
    profileValidation.touchOnChange('countryId');

    clearDependentLookups('states');
    clearDependentLookups('cities');

    if (!countryId) return;

    setIsLoadingStates(true);
    await fetchStates(countryId);
    setIsLoadingStates(false);
  }, [fetchStates, clearDependentLookups, profileValidation]);

  const handleStateChange = useCallback(async (stateId: string) => {
    setFormData(prev => ({
      ...prev,
      stateId,
      cityId: '',
    }));
    profileValidation.touchOnChange('stateId');

    clearDependentLookups('cities');

    if (!stateId) return;

    setIsLoadingCities(true);
    await fetchCities(stateId);
    setIsLoadingCities(false);
  }, [fetchCities, clearDependentLookups, profileValidation]);

  const handleEmployedInChange = useCallback(async (employedInId: string) => {
    setFormData(prev => ({
      ...prev,
      employedInId,
      occupationId: '',
    }));
    profileValidation.touchOnChange('employedInId');

    clearDependentLookups('occupations');

    if (!employedInId) return;

    setIsLoadingOccupations(true);
    await fetchOccupations(employedInId);
    setIsLoadingOccupations(false);
  }, [fetchOccupations, clearDependentLookups, profileValidation]);

  // ============ Family Location Handlers ============

  const handleFamilyCountryChange = useCallback(async (countryId: string) => {
    setFormData(prev => ({
      ...prev,
      familyCountryId: countryId,
      familyStateId: '',
      familyCityId: '',
    }));
    setFamilyStates([]);
    setFamilyCities([]);

    if (!countryId) return;

    setIsLoadingFamilyStates(true);
    const statesData = await sharedService.getStates(countryId);
    setFamilyStates(statesData);
    setIsLoadingFamilyStates(false);
  }, []);

  const handleFamilyStateChange = useCallback(async (stateId: string) => {
    setFormData(prev => ({
      ...prev,
      familyStateId: stateId,
      familyCityId: '',
    }));
    setFamilyCities([]);

    if (!stateId) return;

    setIsLoadingFamilyCities(true);
    const citiesData = await sharedService.getCities(stateId);
    setFamilyCities(citiesData);
    setIsLoadingFamilyCities(false);
  }, []);

  // ============ Sibling Handlers ============

  const handleSiblingCountChange = useCallback((
    field: 'brothers' | 'sisters',
    value: string
  ) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, [field]: sanitized }));
  }, []);

  const canSubmit =
    emailValidation.isValid(formData.email.trim()) &&
    !emailValidation.error &&
    profileValidation.isValid;

  const getSubmitDisabledReason = (): string | undefined => {
    if (isSaving) return 'Saving...';
    if (emailValidation.error) return emailValidation.error;
    return profileValidation.getSubmitDisabledReason();
  };

  const handleSave = async () => {
    if (!userId) return;

    const emailResult = emailValidation.validate(formData.email);
    if (!emailResult.valid) {
      return;
    }

    if (!profileValidation.validateAll()) {
      return;
    }

    setFormError(null);
    setIsSaving(true);
    try {
      const updateData = {
        // Personal Info
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        email: emailResult.trimmed,
        phoneNumber: fullPhoneNumber,
        gender: formData.gender || undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
        aboutYourself: formData.aboutYou || undefined,
        
        // Profile Photos — always send so removals persist on the server
        ...buildProfilePhotoPayload(photoState),
        
        // Religion & Community
        motherTongueId: formData.motherTongueId ? parseInt(formData.motherTongueId) : undefined,
        religionId: formData.religionId ? parseInt(formData.religionId) : undefined,
        sectId: formData.sectId ? parseInt(formData.sectId) : undefined,
        jamatId: formData.jamatId ? parseInt(formData.jamatId) : undefined,
        casteId: formData.casteId ? parseInt(formData.casteId) : undefined,
        haveCasteBar: formData.casteNoBar,
        manglik: formData.manglikId || undefined,
        isHoroscopeNecessary: formData.horoscopeNecessity === 'must',
        
        // Physical Profile
        maratialStatusId: formData.maritalStatusId ? parseInt(formData.maritalStatusId) : undefined,
        heightId: formData.heightId ? parseInt(formData.heightId) : undefined,
        bodyTypeId: formData.bodyTypeId ? parseInt(formData.bodyTypeId) : undefined,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        complexionId: formData.complexionId ? parseInt(formData.complexionId) : undefined,
        bloodGroupId: formData.bloodGroupId ? parseInt(formData.bloodGroupId) : undefined,
        disabilityId: formData.disabilityId ? parseInt(formData.disabilityId) : undefined,
        
        // Location
        countryId: formData.countryId ? parseInt(formData.countryId) : undefined,
        stateId: formData.stateId ? parseInt(formData.stateId) : undefined,
        cityId: formData.cityId ? parseInt(formData.cityId) : undefined,
        
        // Education & Career
        highestDegreeId: formData.degreeId ? parseInt(formData.degreeId) : undefined,
        employedInId: formData.employedInId ? parseInt(formData.employedInId) : undefined,
        occupationId: formData.occupationId ? parseInt(formData.occupationId) : undefined,
        income_currency_id: formData.income_currency_id ? parseInt(formData.income_currency_id) : undefined,
        annualIncome: formData.annualIncome ? parseInt(formData.annualIncome) : undefined,
        companyName: formData.companyName || undefined,
        workLocation: formData.workLocation || undefined,
        
        // Lifestyle
        drinkingHabitId: formData.drinkingHabitId ? parseInt(formData.drinkingHabitId) : undefined,
        smokingHabitId: formData.smokingHabitId ? parseInt(formData.smokingHabitId) : undefined,
        dietId: formData.dietId ? parseInt(formData.dietId) : undefined,
        
        // Family
        familyTypeId: formData.familyTypeId ? parseInt(formData.familyTypeId) : undefined,
        fatherOccupationId: formData.fatherOccupationId ? parseInt(formData.fatherOccupationId) : undefined,
        motherOccupationId: formData.motherOccupationId ? parseInt(formData.motherOccupationId) : undefined,
        brothers: formData.brothers || undefined,
        brothersMarried: formData.marriedBrothers || undefined,
        sisters: formData.sisters || undefined,
        sistersMarried: formData.marriedSisters || undefined,
        familyLivingCountryId: formData.familyCountryId ? parseInt(formData.familyCountryId) : undefined,
        familyLivingStateId: formData.familyStateId ? parseInt(formData.familyStateId) : undefined,
        familyLivingCityId: formData.familyCityId ? parseInt(formData.familyCityId) : undefined,
        address: formData.contactAddress || undefined,
        aboutFamily: formData.aboutFamily || undefined,
      };
      
      await userService.updateUser(userId, updateData);
      
      // Refresh user data after save
      const updatedUser = await userService.getUserById(userId);
      setUser(updatedUser as UserDetailType);
      setIsEditMode(false);
      
      // Reset dynamic fields
      setDynamicFields({ showSect: false, showCaste: false, showJamat: false, showManglik: false });
      setFamilyStates([]);
      setFamilyCities([]);
    } catch (err) {
      console.error('Failed to save user:', err);
      const fieldErrors = getFieldErrorsFromApi(err);
      if (fieldErrors.email) {
        emailValidation.setError(fieldErrors.email);
      } else {
        setFormError(formatApiError(err) || 'Failed to save user');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ============ Assign Plan Handlers ============

  const handleOpenAssignPlanModal = useCallback(async () => {
    setIsAssignPlanModalOpen(true);
    setAssignPlanError(null);
    setAssignPlanSuccess(null);
    setSelectedPlan(null);
    setAssignPlanForm({
      planId: '',
      planDetailId: '',
      noteFromAdmin: '',
      currency: 'INR',
      collectionType: 'online',
    });
    
    // Fetch available plans
    setIsLoadingPlans(true);
    try {
      const plans = await planService.getPlans();
      // Exclude free plans and ordinary hidden plans; keep the reserved dynamic plan even when hidden.
      setAvailablePlans(
        (plans as Array<AdminSubscriptionPlan & { isDynamic?: boolean }>).filter(
          (p) => !p.isFree && (p.isDynamic || !p.isHidden),
        ),
      );
    } catch (err) {
      console.error('Failed to fetch plans:', err);
      setAssignPlanError('Failed to load plans. Please try again.');
    } finally {
      setIsLoadingPlans(false);
    }
  }, []);

  const handleCloseAssignPlanModal = useCallback(() => {
    setIsAssignPlanModalOpen(false);
    setAssignPlanError(null);
    setAssignPlanSuccess(null);
    setSelectedPlan(null);
  }, []);

  const handlePlanChange = useCallback(async (planId: string) => {
    setAssignPlanForm(prev => ({
      ...prev,
      planId,
      planDetailId: '', // Reset variant when plan changes
    }));

    if (!planId) {
      setSelectedPlan(null);
      return;
    }

    // Fetch plan details by ID
    setIsLoadingPlanDetail(true);
    try {
      const plan = await planService.getPlanById(planId);
      setSelectedPlan(plan);
    } catch (err) {
      console.error('Failed to fetch plan details:', err);
      setAssignPlanError('Failed to load plan details. Please try again.');
      setSelectedPlan(null);
    } finally {
      setIsLoadingPlanDetail(false);
    }
  }, []);

  const handleVariantChange = useCallback((variantId: string) => {
    setAssignPlanForm(prev => ({
      ...prev,
      planDetailId: variantId,
    }));
  }, []);

  const handleAssignPlanSubmit = useCallback(async () => {
    if (!userId || !assignPlanForm.planDetailId) {
      setAssignPlanError('Please select a plan variant');
      return;
    }
    setIsAssigningPlan(true);
    setAssignPlanError(null);

    try {
      await userService.assignPlan({
        user_id: parseInt(userId),
        plan_detail_id: parseInt(assignPlanForm.planDetailId),
        note_from_admin: assignPlanForm.noteFromAdmin,
        currency: assignPlanForm.currency,
        collection_type: assignPlanForm.collectionType,
      });

      setAssignPlanSuccess('Plan assigned successfully!');
      
      // Refresh user details to show updated plan info
      const updatedUser = await userService.getUserById(userId);
      setUser(updatedUser as UserDetailType);
      
      // Close modal after a short delay
      setTimeout(() => {
        setIsAssignPlanModalOpen(false);
        setAssignPlanSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('Failed to assign plan:', err);
      setAssignPlanError(err instanceof Error ? err.message : 'Failed to assign plan. Please try again.');
    } finally {
      setIsAssigningPlan(false);
    }
  }, [userId, assignPlanForm]);

  // Get selected variant details
  const selectedVariant = selectedPlan?.variants?.find(
    (v) => String(v.planDetailId) === assignPlanForm.planDetailId
  ) || null;

  const selectedVariantPrice = useMemo(() => {
    if (!selectedVariant) return null;
    return getVariantPriceByCurrency(
      selectedVariant.price,
      assignPlanForm.currency as PlanCurrencyCode
    );
  }, [selectedVariant, assignPlanForm.currency]);

  // Currency options for assign plan
  const currencyOptions: SelectOption[] = [
    { value: 'INR', label: '₹ INR' },
    { value: 'USD', label: '$ USD' },
    { value: 'CAD', label: '$ CAD' },
    { value: 'EUR', label: '€ EUR' },
  ];

  const collectionTypeOptions: SelectOption[] = [
    { value: 'online', label: 'Online' },
    { value: 'cash', label: 'Cash' },
  ];

  // ============ Photo Upload Handlers ============

  const validateImageFile = useCallback((file: File): string | null => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Only JPEG, PNG, and WebP images are allowed';
    }
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return 'Image size must be less than 5MB';
    }
    return null;
  }, []);

  const createPreviewUrl = useCallback((file: File): string => {
    return URL.createObjectURL(file);
  }, []);

  const initializePhotosFromUser = useCallback((userData: UserDetailType) => {
    const mainPhotoUrl = userData.userImage || null;
    const mainPhotoId = userData.profilePictureId && userData.profilePictureId > 0
      ? userData.profilePictureId
      : 0;
    const galleryPhotos = userData.otherImages?.length
      ? userData.otherImages.map((image) => ({
          id: Number(image.file_id),
          url: image.url,
          fileName: image.file_name,
        }))
      : (userData.photos || []).map((url, index) => ({ id: index, url }));

    setPhotoState({
      mainPhoto: mainPhotoUrl ? { id: mainPhotoId, url: mainPhotoUrl } : null,
      mainPhotoPreview: mainPhotoUrl,
      galleryPhotos,
      galleryPreviews: galleryPhotos.map((photo) => photo.url),
    });
  }, []);

  const uploadMainPhoto = useCallback(
    async (file: File) => {
      setPhotoError(null);
      setIsUploadingMainPhoto(true);

      const previewUrl = createPreviewUrl(file);
      setPhotoState((prev) => ({
        ...prev,
        mainPhotoPreview: previewUrl,
      }));

      try {
        const uploadedFiles = await sharedService.uploadFiles([file]);

        if (uploadedFiles.length > 0) {
          const uploadedFile = uploadedFiles[0];
          await sharedService.uploadUserImage(uploadedFile.id);

          setPhotoState((prev) => ({
            ...prev,
            mainPhoto: uploadedFile,
            mainPhotoPreview: uploadedFile.url,
          }));
        }
      } catch (err) {
        console.error('Failed to upload main photo:', err);
        setPhotoError('Failed to upload photo. Please try again.');
        setPhotoState((prev) => ({
          ...prev,
          mainPhotoPreview: prev.mainPhoto?.url || null,
        }));
        throw err;
      } finally {
        setIsUploadingMainPhoto(false);
      }
    },
    [createPreviewUrl]
  );

  const {
    isCropOpen,
    cropImageSrc,
    cropFileMeta,
    isApplyingCrop,
    closeCrop,
    handleCropApply,
    handleFileInputChange: handleMainPhotoSelect,
  } = usePhotoCropFlow({
    validateImageFile,
    onUpload: uploadMainPhoto,
    onValidationError: setPhotoError,
  });

  const uploadGalleryFileAtSlot = useCallback(
    async (file: File, slotIndex: number) => {
      setPhotoError(null);
      const previewUrl = createPreviewUrl(file);

      setPhotoState((prev) => {
        const galleryPreviews = [...prev.galleryPreviews];
        galleryPreviews[slotIndex] = previewUrl;
        return { ...prev, galleryPreviews };
      });

      try {
        const uploadedFiles = await sharedService.uploadFiles([file]);
        const uploadedFile = uploadedFiles[0];
        if (!uploadedFile?.id) {
          throw new Error('Failed to upload photo.');
        }

        await sharedService.uploadUserOtherImages([uploadedFile.id]);

        setPhotoState((prev) => {
          const galleryPhotos = [...prev.galleryPhotos];
          const galleryPreviews = [...prev.galleryPreviews];
          galleryPhotos[slotIndex] = uploadedFile;
          galleryPreviews[slotIndex] = uploadedFile.url;
          return { ...prev, galleryPhotos, galleryPreviews };
        });
      } catch (err) {
        console.error('Failed to upload gallery photo:', err);
        setPhotoError('Failed to upload photo. Please try again.');
        setPhotoState((prev) => ({
          ...prev,
          galleryPreviews: prev.galleryPhotos.map((photo) => photo?.url ?? null),
        }));
        throw err;
      }
    },
    [createPreviewUrl]
  );

  const {
    galleryInputRef,
    openGalleryPicker,
    handleGalleryFileChange,
    isGallerySlotUploading,
    isCropOpen: isGalleryCropOpen,
    cropImageSrc: galleryCropImageSrc,
    cropFileMeta: galleryCropFileMeta,
    isApplyingCrop: isApplyingGalleryCrop,
    closeCrop: closeGalleryCrop,
    handleCropApply: handleGalleryCropApply,
  } = useGalleryPhotoCropUpload({
    validateImageFile,
    uploadGalleryFile: uploadGalleryFileAtSlot,
    onValidationError: setPhotoError,
  });

  const handleRemoveMainPhoto = useCallback(() => {
    setPhotoState(prev => ({
      ...prev,
      mainPhoto: null,
      mainPhotoPreview: null,
    }));
    setPhotoError(null);
  }, []);

  const handleRemoveGalleryPhoto = useCallback((index: number) => {
    setPhotoState(prev => ({
      ...prev,
      galleryPhotos: prev.galleryPhotos.filter((_, i) => i !== index),
      galleryPreviews: prev.galleryPreviews.filter((_, i) => i !== index),
    }));
  }, []);

  const triggerMainPhotoInput = useCallback(() => {
    mainPhotoInputRef.current?.click();
  }, []);

  const handleGallerySlotClick = useCallback((slotIndex: number) => {
    if (photoState.galleryPreviews[slotIndex]) {
      return;
    }
    if (photoState.galleryPhotos.length >= MAX_GALLERY_PHOTOS) {
      setPhotoError(`Maximum ${MAX_GALLERY_PHOTOS} gallery photos allowed`);
      return;
    }
    openGalleryPicker(slotIndex);
  }, [openGalleryPicker, photoState.galleryPhotos.length, photoState.galleryPreviews]);

  useEffect(() => {
    if (!userId) {
      setError('User ID is required');
      setIsLoading(false);
      return;
    }

    const fetchUser = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const userData = await userService.getUserById(userId);
        setUser(userData as UserDetailType);
      } catch (err: unknown) {
        console.warn('Failed to fetch user, using mock profile:', err);
        setUser(getMockClientProfile(userId));
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  if (isLoading || isPreparingEdit || (isEditRequested && !isEditMode)) {
    return (
      <div className="user-detail-page">
        <LoadingState
          message={
            isPreparingEdit || isEditRequested
              ? 'Opening editor...'
              : 'Loading user details...'
          }
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-detail-page">
        <ErrorState message={error} onRetry={handleBack} retryLabel="Go Back" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-detail-page">
        <ErrorState message="User not found" onRetry={handleBack} retryLabel="Go Back" />
      </div>
    );
  }

  const profileImage = user.userImage || user.avatar;
  const fullName = getFullName(user.firstName, user.middleName, user.lastName);

  return (
    <div className={`user-detail-page ${isEditMode ? 'user-detail-page--edit' : 'user-detail-page--view'}`}>
      {isEditMode && (
        <PageHeader
          title="Edit Profile"
          subtitle="Update client information across photos, details, career, and family."
          backButton={{ label: 'Back to Client List', onClick: handleCancelEdit }}
        />
      )}

      <div className={`user-detail-content ${isEditMode ? 'user-detail-content--edit-mode' : ''}`}>
        {!isEditMode && (
          <ClientProfileView
            user={user}
            fullName={fullName}
            profileImage={profileImage}
            onBack={handleBack}
            onAssignPlan={handleOpenAssignPlanModal}
            onEdit={handleEditClick}
            canEditProfile={canEditProfile}
            canAssignPlan={canAssignPlan}
            canViewTasksTab={canViewTasksTab}
          />
        )}

        {/* Top Section - Profile Photo and Photo Gallery */}
        {isEditMode && (
          <InfoCard
            title="1. Profile Photos"
            iconSrc={CREATE_USER_SECTION_ICONS.camera}
            iconAlt=""
            className="create-user-section create-user-section--photos"
          >
            <div className="photo-upload-section">
              <input
                ref={mainPhotoInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleMainPhotoSelect}
                style={{ display: 'none' }}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleGalleryFileChange}
                style={{ display: 'none' }}
              />

              {photoError && (
                <div className="photo-error">{photoError}</div>
              )}

              <div className="photo-slots-row">
                {Array.from({ length: TOTAL_PHOTO_SLOTS }).map((_, index) => {
                  const isPrimary = index === 0;
                  const galleryIndex = index - 1;
                  const galleryPreview = !isPrimary
                    ? photoState.galleryPreviews[galleryIndex]
                    : null;
                  const preview = isPrimary
                    ? photoState.mainPhotoPreview
                    : galleryPreview;
                  const isUploading = isPrimary
                    ? isUploadingMainPhoto || isApplyingCrop
                    : isGallerySlotUploading(galleryIndex);

                  return (
                    <div
                      key={index}
                      className={`photo-slot ${isPrimary ? 'photo-slot--primary primary-photo-slot' : ''} ${preview ? 'photo-slot--filled' : ''} ${isUploading ? 'photo-slot--uploading' : ''}`}
                      onClick={() => {
                        if (isUploading) return;
                        if (isPrimary) {
                          triggerMainPhotoInput();
                        } else {
                          handleGallerySlotClick(galleryIndex);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (isPrimary) triggerMainPhotoInput();
                          else handleGallerySlotClick(galleryIndex);
                        }
                      }}
                    >
                      {isUploading ? (
                        <Spinner size="small" />
                      ) : preview ? (
                        <>
                          <img src={preview} alt="" className="photo-slot__image" />
                          <button
                            type="button"
                            className="photo-slot__remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isPrimary) handleRemoveMainPhoto();
                              else handleRemoveGalleryPhoto(galleryIndex);
                            }}
                            aria-label="Remove photo"
                          >
                            <TrashIcon />
                          </button>
                        </>
                      ) : (
                        <div className="photo-slot__placeholder">
                          {isPrimary ? (
                            <>
                              <img
                                src={CREATE_USER_SECTION_ICONS.camera}
                                alt=""
                                className="photo-slot__asset-icon"
                              />
                              <span className="photo-slot__label">Primary Photo*</span>
                            </>
                          ) : (
                            <PlusIcon className="photo-slot__icon" />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </InfoCard>
        )}

        {isEditMode && (
          <>
            {formError && (
              <div className="create-user-error" role="alert">
                {formError}
              </div>
            )}

            <InfoCard
              title="2. Profile Details"
              iconSrc={CREATE_USER_SECTION_ICONS.person}
              iconAlt=""
              className="create-user-section"
            >
              <div className="profile-form">
                <div className="form-row form-row--3">
                  <Input label="First Name" value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} onBlur={() => profileValidation.touch('firstName')} error={profileValidation.showError('firstName')} placeholder="John" required />
                  <Input label="Middle Name" value={formData.middleName} onChange={(e) => handleInputChange('middleName', e.target.value)} placeholder="Quincy" />
                  <Input label="Last Name" value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} onBlur={() => profileValidation.touch('lastName')} error={profileValidation.showError('lastName')} placeholder="Doe" required />
                </div>
                <div className="form-row form-row--3">
                  <PhoneInput
                    label="Contact Number"
                    value={formData.phoneE164 || undefined}
                    onChange={handlePhoneChange}
                    onBlur={() => profileValidation.touch('phoneNumber')}
                    error={profileValidation.showError('phoneNumber')}
                    placeholder="9876543210"
                    defaultCountry="IN"
                    international
                    required
                  />
                  <Input
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      handleInputChange('email', e.target.value);
                      emailValidation.onChange(e.target.value);
                    }}
                    onBlur={() => emailValidation.onBlur(formData.email)}
                    error={emailValidation.error}
                    placeholder="john.doe@example.com"
                    required
                  />
                  <Select label="Gender" placeholder="Select Gender" value={formData.gender} onChange={(value) => handleInputChange('gender', value)} options={genderOptions} error={profileValidation.showError('gender')} required />
                </div>
                <div className="form-row form-row--3">
                  <Input label="Date of Birth" type="date" value={formData.dateOfBirth} onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} onBlur={() => profileValidation.touch('dateOfBirth')} error={profileValidation.showError('dateOfBirth')} max={getMaxDateOfBirthForMinAge()} required />
                  <Select label="Mother Tongue" placeholder="Select Language" value={formData.motherTongueId} onChange={(value) => handleInputChange('motherTongueId', value)} groups={motherTongues} searchable error={profileValidation.showError('motherTongueId')} required />
                  <Select label="Religion" placeholder="Select Religion" value={formData.religionId} onChange={handleReligionChange} options={religions} error={profileValidation.showError('religionId')} required />
                </div>
                {dynamicFields.showSect && (
                  <div className="form-row form-row--3">
                    <Select label="Sect" placeholder={isLoadingSects ? 'Loading...' : 'Select Sect'} value={formData.sectId} onChange={handleSectChange} options={sects} disabled={isLoadingSects || sects.length === 0} error={profileValidation.showError('sectId')} required />
                  </div>
                )}
                {(dynamicFields.showCaste || (dynamicFields.showSect && formData.sectId)) && (
                  <div className="form-row form-row--2">
                    <Select label="Caste" placeholder={isLoadingCastes ? 'Loading...' : 'Caste'} value={formData.casteId} onChange={(value) => handleInputChange('casteId', value)} options={castes} searchable disabled={isLoadingCastes || castes.length === 0 || formData.casteNoBar} error={profileValidation.showError('casteId')} required={!formData.casteNoBar} />
                    <div className="checkbox-field">
                      <label className="checkbox-label">
                        <input type="checkbox" checked={formData.casteNoBar} onChange={(e) => {
                          handleInputChange('casteNoBar', e.target.checked);
                          if (e.target.checked) handleInputChange('casteId', '');
                        }} />
                        <span>Caste no bar (I am open to marry people of all castes)</span>
                      </label>
                    </div>
                  </div>
                )}
                {dynamicFields.showJamat && (
                  <div className="form-row form-row--2">
                    <Select label="Jamat" placeholder={isLoadingJamats ? 'Loading...' : 'Jamat'} value={formData.jamatId} onChange={(value) => handleInputChange('jamatId', value)} options={jamats} disabled={isLoadingJamats || jamats.length === 0 || !formData.sectId} error={profileValidation.showError('jamatId')} required />
                  </div>
                )}
                <div className="form-row form-row--3">
                  {dynamicFields.showManglik && (
                    <Select
                      label="Are you manglik?"
                      placeholder="No"
                      value={formData.manglikId}
                      onChange={(value) => handleInputChange('manglikId', value)}
                      options={manglikOptions}
                      error={profileValidation.showError('manglikId')}
                      required
                    />
                  )}
                  <Select
                    label="Horoscope?"
                    placeholder="Select"
                    value={formData.horoscopeNecessity}
                    onChange={(value) => handleInputChange('horoscopeNecessity', value)}
                    options={horoscopeOptions}
                    error={profileValidation.showError('horoscopeNecessity')}
                    required
                  />
                </div>
                <div className="form-row form-row--3">
                  <Select label="Marital Status" placeholder="Never Married" value={formData.maritalStatusId} onChange={(value) => handleInputChange('maritalStatusId', value)} options={maritalStatuses} error={profileValidation.showError('maritalStatusId')} required />
                  <Select label="Height" placeholder="Select Height" value={formData.heightId} onChange={(value) => handleInputChange('heightId', value)} options={heights} searchable error={profileValidation.showError('heightId')} required />
                  <Select label="Body Type" placeholder="Average" value={formData.bodyTypeId} onChange={(value) => handleInputChange('bodyTypeId', value)} options={bodyTypes} />
                </div>
                <div className="form-row form-row--3">
                  <Input label="Weight (kg)" type="text" inputMode="decimal" value={formData.weight} onChange={(e) => handleInputChange('weight', e.target.value)} placeholder="70" />
                  <Select label="Complexion" placeholder="Fair" value={formData.complexionId} onChange={(value) => handleInputChange('complexionId', value)} options={complexions} />
                  <Select label="Blood Group" placeholder="O+" value={formData.bloodGroupId} onChange={(value) => handleInputChange('bloodGroupId', value)} options={bloodGroups} />
                </div>
                <div className="form-row form-row--3">
                  <Select label="Disability Status" placeholder="None" value={formData.disabilityId} onChange={(value) => handleInputChange('disabilityId', value)} options={disabilities} />
                </div>
              </div>
            </InfoCard>

            <InfoCard title="3. Career Details" iconSrc={CREATE_USER_SECTION_ICONS.workBag} iconAlt="" className="create-user-section">
              <div className="profile-form">
                <div className="form-row form-row--3">
                  <Select label="Country" placeholder="Select Country" value={formData.countryId} onChange={handleCountryChange} options={countries} searchable error={profileValidation.showError('countryId')} required />
                  <Select label="Employed In" placeholder="Private Sector" value={formData.employedInId} onChange={handleEmployedInChange} options={employedIn} error={profileValidation.showError('employedInId')} required />
                  <Select label="State" placeholder={isLoadingStates ? 'Loading...' : 'Maharashtra'} value={formData.stateId} onChange={handleStateChange} options={states} searchable disabled={!formData.countryId || isLoadingStates} error={profileValidation.showError('stateId')} required />
                </div>
                <div className="form-row form-row--3">
                  <Select label="City" placeholder={isLoadingCities ? 'Loading...' : 'Mumbai'} value={formData.cityId} onChange={(value) => handleInputChange('cityId', value)} options={cities} searchable disabled={!formData.stateId || isLoadingCities} error={profileValidation.showError('cityId')} required />
                  <Select label="Occupation" placeholder={isLoadingOccupations ? 'Loading...' : 'Software Engineer'} value={formData.occupationId} onChange={(value) => handleInputChange('occupationId', value)} groups={occupations} searchable disabled={!formData.employedInId || isLoadingOccupations} error={profileValidation.showError('occupationId')} required />
                  <Select label="Highest Degree" placeholder="B.Tech / MBA / MBBS" value={formData.degreeId} onChange={(value) => handleInputChange('degreeId', value)} groups={degrees} searchable error={profileValidation.showError('degreeId')} required />
                </div>
                <div className="form-row form-row--1">
                  <div
                    className={`income-field${
                      profileValidation.showError('income_currency_id') ||
                      profileValidation.showError('annualIncome')
                        ? ' income-field--error'
                        : ''
                    }`}
                  >
                    <label className="income-field__label">
                      Annual Income <span className="required-mark">*</span>
                    </label>
                    <div className="income-field__inputs">
                      <Select
                        placeholder={isLoadingCurrencies ? 'Loading...' : 'Currency'}
                        value={formData.income_currency_id}
                        onChange={(value) => handleInputChange('income_currency_id', value)}
                        options={incomeCurrencies}
                        disabled={isLoadingCurrencies}
                        searchable={false}
                        fullWidth={false}
                        className="income-field__currency"
                        required
                      />
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={formData.annualIncome}
                        onChange={(e) => handleAnnualIncomeChange(e.target.value)}
                        onBlur={() => profileValidation.touch('annualIncome')}
                        placeholder="1,200,000"
                        className="income-field__amount"
                        fullWidth={false}
                        required
                      />
                    </div>
                    {(profileValidation.showError('income_currency_id') ||
                      profileValidation.showError('annualIncome')) && (
                      <span className="income-field__error" role="alert">
                        {profileValidation.showError('income_currency_id') ||
                          profileValidation.showError('annualIncome')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="form-row form-row--1">
                  <div className="textarea-field">
                    <label className="textarea-label">About You</label>
                    <textarea value={formData.aboutYou} onChange={(e) => handleInputChange('aboutYou', e.target.value)} placeholder="Describe yourself, your interests, and what you are looking for in a partner..." rows={4} />
                  </div>
                </div>
              </div>
            </InfoCard>

            <InfoCard title="4. Lifestyle & Family" iconSrc={CREATE_USER_SECTION_ICONS.lifestyleTree} iconAlt="" className="create-user-section">
              <div className="profile-form">
                <div className="form-row form-row--3">
                  <Select label="Do you Drink?" placeholder="No" value={formData.drinkingHabitId} onChange={(value) => handleInputChange('drinkingHabitId', value)} options={drinkingHabits} error={profileValidation.showError('drinkingHabitId')} required />
                  <Select label="Do you Smoke?" placeholder="No" value={formData.smokingHabitId} onChange={(value) => handleInputChange('smokingHabitId', value)} options={smokingHabits} error={profileValidation.showError('smokingHabitId')} required />
                  <Select label="Diet" placeholder="Vegetarian" value={formData.dietId} onChange={(value) => handleInputChange('dietId', value)} options={diets} error={profileValidation.showError('dietId')} required />
                </div>
                <div className="form-row form-row--3">
                  <Select label="Family Type" placeholder="Joint Family" value={formData.familyTypeId} onChange={(value) => handleInputChange('familyTypeId', value)} options={familyTypes} />
                  <Select label="Family Living In — Country" placeholder="Country" value={formData.familyCountryId} onChange={handleFamilyCountryChange} options={countries} searchable />
                  <Select label="State" placeholder={isLoadingFamilyStates ? 'Loading...' : 'State'} value={formData.familyStateId} onChange={handleFamilyStateChange} options={familyStates} searchable disabled={!formData.familyCountryId || isLoadingFamilyStates} />
                </div>
                <div className="form-row form-row--3">
                  <Select label="City" placeholder={isLoadingFamilyCities ? 'Loading...' : 'City'} value={formData.familyCityId} onChange={(value) => handleInputChange('familyCityId', value)} options={familyCities} searchable disabled={!formData.familyStateId || isLoadingFamilyCities} />
                  <Select label="Father's Occupation" placeholder="Retired / Business" value={formData.fatherOccupationId} onChange={(value) => handleInputChange('fatherOccupationId', value)} options={fatherOccupations} />
                  <Select label="Mother's Occupation" placeholder="Homemaker / Teacher" value={formData.motherOccupationId} onChange={(value) => handleInputChange('motherOccupationId', value)} options={motherOccupations} />
                </div>
                <div className="form-row form-row--1">
                  <div className="textarea-field">
                    <label className="textarea-label">Full Contact Address</label>
                    <textarea value={formData.contactAddress} onChange={(e) => handleInputChange('contactAddress', e.target.value)} placeholder="Enter detailed residence address..." rows={3} />
                  </div>
                </div>
                <div className="form-row form-row--2">
                  <Input label="Brothers Count" type="text" inputMode="numeric" value={formData.brothers} onChange={(e) => handleSiblingCountChange('brothers', e.target.value)} placeholder="0" />
                  <Input label="Sisters Count" type="text" inputMode="numeric" value={formData.sisters} onChange={(e) => handleSiblingCountChange('sisters', e.target.value)} placeholder="0" />
                </div>
                <div className="form-row form-row--1">
                  <div className="textarea-field">
                    <label className="textarea-label">About Family</label>
                    <textarea value={formData.aboutFamily} onChange={(e) => handleInputChange('aboutFamily', e.target.value)} placeholder="Briefly describe your family values, background, and members..." rows={4} />
                  </div>
                </div>
              </div>
            </InfoCard>

            <div className="form-actions-footer">
              <Button variant="secondary" onClick={handleCancelEdit} disabled={isSaving} className="form-actions-footer__cancel">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canSubmit || isSaving} disabledReason={getSubmitDisabledReason()} className="form-actions-footer__submit">
                {isSaving ? <Spinner size="small" /> : null}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Assign Plan Modal */}
      {isAssignPlanModalOpen && (
        <div className="modal-overlay" onClick={handleCloseAssignPlanModal}>
          <div className="modal-content assign-plan-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Plan to User</h2>
              <button className="modal-close" onClick={handleCloseAssignPlanModal}>
                <Cross2Icon />
              </button>
            </div>

            <div className="modal-body">
              {assignPlanError && (
                <div className="modal-error">{assignPlanError}</div>
              )}
              
              {assignPlanSuccess && (
                <div className="modal-success">{assignPlanSuccess}</div>
              )}

              {isLoadingPlans ? (
                <div className="modal-loading">
                  <Spinner size="medium" />
                  <span>Loading plans...</span>
                </div>
              ) : (
                <div className="assign-plan-form">
                  <div className="assign-plan-form__row">
                    <Select
                      label="Select Plan"
                      placeholder="Choose a plan"
                      value={assignPlanForm.planId}
                      onChange={handlePlanChange}
                      options={availablePlans.map((p) => ({
                        value: p.planId,
                        label: p.name,
                      }))}
                      required
                    />

                    {isLoadingPlanDetail ? (
                      <div className="plan-detail-loading">
                        <Spinner size="small" />
                        <span>Loading plan details...</span>
                      </div>
                    ) : assignPlanForm.planId && selectedPlan?.variants?.length === 0 ? (
                      <div className="no-variants">No variants available for this plan</div>
                    ) : (
                      <Select
                        label="Select Duration"
                        placeholder="Choose plan duration"
                        value={assignPlanForm.planDetailId}
                        onChange={handleVariantChange}
                        options={(selectedPlan?.variants ?? []).map((v) => ({
                          value: String(v.planDetailId),
                          label: `${v.durationMonths} Month${v.durationMonths > 1 ? 's' : ''}`,
                        }))}
                        disabled={!assignPlanForm.planId || isLoadingPlanDetail}
                        required
                      />
                    )}

                    <Select
                      label="Currency"
                      placeholder="Select currency"
                      value={assignPlanForm.currency}
                      onChange={(value) => setAssignPlanForm(prev => ({ ...prev, currency: value }))}
                      options={currencyOptions}
                      required
                    />

                    <Select
                      label="Collection Type"
                      placeholder="Select collection type"
                      value={assignPlanForm.collectionType}
                      onChange={(value) =>
                        setAssignPlanForm((prev) => ({
                          ...prev,
                          collectionType: value as 'online' | 'cash',
                        }))
                      }
                      options={collectionTypeOptions}
                      required
                    />
                  </div>

                  {/* Selected Plan Details Display */}
                  {selectedVariant && (
                    <div className="selected-plan-details">
                      <h4>Selected Plan Details</h4>
                      <div className="plan-detail-info">
                        <div className="plan-detail-row">
                          <span className="plan-detail-label">Plan:</span>
                          <span className="plan-detail-value">{selectedPlan?.name}</span>
                        </div>
                        <div className="plan-detail-row">
                          <span className="plan-detail-label">Duration:</span>
                          <span className="plan-detail-value">
                            {selectedVariant.durationMonths} Month{selectedVariant.durationMonths > 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="plan-detail-row">
                          <span className="plan-detail-label">Price:</span>
                          <span className="plan-detail-value">
                            {selectedVariantPrice
                              ? formatInvoiceAmount(
                                  selectedVariantPrice.amount,
                                  assignPlanForm.currency
                                )
                              : '—'}
                          </span>
                        </div>
                        <div className="plan-detail-row">
                          <span className="plan-detail-label">Collection:</span>
                          <span className="plan-detail-value">
                            {assignPlanForm.collectionType === 'cash' ? 'Cash' : 'Online'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Note from Admin */}
                  <div className="form-field">
                    <label className="form-label">Note from Admin</label>
                    <textarea
                      className="about-textarea"
                      value={assignPlanForm.noteFromAdmin}
                      onChange={(e) => setAssignPlanForm(prev => ({ ...prev, noteFromAdmin: e.target.value }))}
                      placeholder="Add a note (optional)..."
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <Button 
                variant="secondary" 
                onClick={handleCloseAssignPlanModal}
                disabled={isAssigningPlan}
              >
                Cancel
              </Button>
              <Button
                className="client-profile-assign"
                onClick={handleAssignPlanSubmit}
                disabled={isAssigningPlan || isLoadingPlans || isLoadingPlanDetail || !assignPlanForm.planDetailId}
              >
                {isAssigningPlan ? (
                  <>
                    <Spinner size="small" />
                    Assigning...
                  </>
                ) : (
                  'Assign Plan'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <PhotoCropModal
        isOpen={isCropOpen}
        imageSrc={cropImageSrc}
        fileName={cropFileMeta.fileName}
        mimeType={cropFileMeta.mimeType}
        onClose={closeCrop}
        onApply={handleCropApply}
        isApplying={isApplyingCrop || isUploadingMainPhoto}
      />
      <PhotoCropModal
        isOpen={isGalleryCropOpen}
        imageSrc={galleryCropImageSrc}
        fileName={galleryCropFileMeta.fileName}
        mimeType={galleryCropFileMeta.mimeType}
        onClose={closeGalleryCrop}
        onApply={handleGalleryCropApply}
        isApplying={isApplyingGalleryCrop}
      />
    </div>
  );
};
